import type { PlacePeriodReport, StatisticStatus } from './types';

export type RatingTrendDirection = 'up' | 'flat' | 'down';

export type RatingTrend = {
  direction: RatingTrendDirection;
  label: 'Trending up' | 'Flat' | 'Trending down';
  lifetimeRating: number;
};

export type ScorecardOpportunity = {
  id: 'reviews' | 'rating' | 'response-rate' | 'response-time';
  title: string;
  value: string;
  action: string;
  status: StatisticStatus;
};

type OpportunityPeriod = Pick<
  PlacePeriodReport,
  | 'review_count'
  | 'review_count_status'
  | 'average_rating'
  | 'average_rating_status'
  | 'response_rate'
  | 'response_rate_status'
  | 'avg_response_time'
  | 'avg_response_time_status'
>;

const STATUS_ORDER: StatisticStatus[] = [
  'extremely-low',
  'below-average',
  'average',
  'above-average',
];

function statisticStatus(value: string): StatisticStatus {
  if (
    value === 'extremely-low' ||
    value === 'below-average' ||
    value === 'average' ||
    value === 'above-average'
  ) {
    return value;
  }
  return 'average';
}

function reviewWeight(period: OpportunityPeriod): number {
  return Number.isFinite(period.review_count) ? Math.max(0, period.review_count) : 0;
}

function weightedAverage(
  periods: readonly OpportunityPeriod[],
  value: (period: OpportunityPeriod) => number | null,
): number {
  let weightedTotal = 0;
  let totalWeight = 0;

  for (const period of periods) {
    const weight = reviewWeight(period);
    const periodValue = value(period);
    if (weight > 0 && periodValue != null && Number.isFinite(periodValue)) {
      weightedTotal += periodValue * weight;
      totalWeight += weight;
    }
  }

  return totalWeight > 0 ? weightedTotal / totalWeight : 0;
}

function weightedStatus(
  periods: readonly OpportunityPeriod[],
  status: (period: OpportunityPeriod) => string,
): StatisticStatus {
  const totalWeight = periods.reduce((sum, period) => sum + reviewWeight(period), 0);
  if (totalWeight === 0) {
    return periods.reduce<StatisticStatus>((lowest, period) => {
      const candidate = statisticStatus(status(period));
      return STATUS_ORDER.indexOf(candidate) < STATUS_ORDER.indexOf(lowest) ? candidate : lowest;
    }, 'above-average');
  }

  const weightedRank = periods.reduce((sum, period) => {
    return sum + STATUS_ORDER.indexOf(statisticStatus(status(period))) * reviewWeight(period);
  }, 0);
  return STATUS_ORDER[Math.round(weightedRank / totalWeight)] ?? 'average';
}

function responseTimeDays(value: string): number | null {
  if (/^<\s*24 hours$/i.test(value)) {
    return 0.5;
  }

  const days = value.match(/^([0-9]+(?:\.[0-9]+)?)\s+days?$/i);
  if (days) {
    return Number(days[1]);
  }

  const hours = value.match(/^([0-9]+(?:\.[0-9]+)?)\s+hours?$/i);
  return hours ? Number(hours[1]) / 24 : null;
}

function formatResponseTime(days: number | null): string {
  if (days == null) {
    return 'N/A';
  }
  if (days < 1) {
    return '< 24 hours';
  }

  const roundedDays = Number(days.toFixed(1));
  return `${roundedDays.toLocaleString()} ${roundedDays === 1 ? 'day' : 'days'}`;
}

export function getRatingTrend(
  periodRating?: number | null,
  lifetimeRating?: number | null,
): RatingTrend | null {
  if (
    periodRating == null ||
    lifetimeRating == null ||
    !Number.isFinite(periodRating) ||
    !Number.isFinite(lifetimeRating)
  ) {
    return null;
  }

  const displayedPeriod = Number(periodRating.toFixed(2));
  const displayedLifetime = Number(lifetimeRating.toFixed(2));
  if (displayedPeriod > displayedLifetime) {
    return { direction: 'up', label: 'Trending up', lifetimeRating };
  }
  if (displayedPeriod < displayedLifetime) {
    return { direction: 'down', label: 'Trending down', lifetimeRating };
  }
  return { direction: 'flat', label: 'Flat', lifetimeRating };
}

export function getScorecardOpportunities(
  periods: readonly OpportunityPeriod[],
  rangeDays: number,
): ScorecardOpportunity[] {
  if (periods.length === 0) {
    return [];
  }

  const reviewCount = periods.reduce((sum, period) => sum + reviewWeight(period), 0);
  const averageRating = weightedAverage(periods, (period) => period.average_rating);
  const weightedResponseRate = weightedAverage(periods, (period) => period.response_rate);
  const responseTimePeriods = periods.filter(
    (period) => reviewWeight(period) > 0 && responseTimeDays(period.avg_response_time) != null,
  );
  const averageResponseTime =
    responseTimePeriods.length > 0
      ? weightedAverage(responseTimePeriods, (period) => responseTimeDays(period.avg_response_time))
      : null;
  const period = {
    review_count: reviewCount,
    review_count_status: weightedStatus(periods, (candidate) => candidate.review_count_status),
    average_rating: averageRating,
    average_rating_status: weightedStatus(periods, (candidate) => candidate.average_rating_status),
    response_rate: weightedResponseRate,
    response_rate_status: weightedStatus(periods, (candidate) => candidate.response_rate_status),
    avg_response_time: formatResponseTime(averageResponseTime),
    avg_response_time_status: weightedStatus(
      periods,
      (candidate) => candidate.avg_response_time_status,
    ),
  } satisfies OpportunityPeriod;
  const reviewStatus = statisticStatus(period.review_count_status);
  const ratingStatus = statisticStatus(period.average_rating_status);
  const responseRateStatus = statisticStatus(period.response_rate_status);
  const responseTimeStatus = statisticStatus(period.avg_response_time_status);
  const monthlyReviews = rangeDays > 0 ? Math.round((period.review_count / rangeDays) * 30) : 0;
  const reviewTarget = monthlyReviews < 15 ? 30 : monthlyReviews * 2;
  const ratingTarget = period.average_rating < 4.5 ? 4.5 : 4.6;
  const responseRate = Math.round(period.response_rate * 100);
  const opportunities: ScorecardOpportunity[] = [
    {
      id: 'reviews',
      title: 'Build a steadier review flow',
      value: `${monthlyReviews.toLocaleString()} \u2192 ${reviewTarget.toLocaleString()}+ reviews/month`,
      action:
        monthlyReviews < 15
          ? 'Build to at least 30 reviews/month across all sources.'
          : 'Generate 2X+ more reviews across all sources.',
      status: reviewStatus,
    },
  ];

  if (period.average_rating < 4.6) {
    opportunities.push({
      id: 'rating',
      title: 'Lift the average rating',
      value: `${period.average_rating.toFixed(2)} \u2192 ${ratingTarget.toFixed(1)}+`,
      action: 'Improve your average rating.',
      status: ratingStatus,
    });
  }

  if (responseRate < 95) {
    opportunities.push({
      id: 'response-rate',
      title: 'Respond more consistently',
      value: `${responseRate}% \u2192 100%`,
      action: 'Respond to every review.',
      status: responseRateStatus,
    });
  }

  if (averageResponseTime != null && averageResponseTime > 2) {
    opportunities.push({
      id: 'response-time',
      title: 'Shorten response time',
      value: `${period.avg_response_time} \u2192 <1 day`,
      action: 'Respond within one business day.',
      status: responseTimeStatus,
    });
  }

  return opportunities;
}
